'use client';

import { twMerge } from 'tailwind-merge';
import Container from '../Container';
import Title from './title';
import Description from './description';
import MediaImage from './media-image';
import MediaVideo from './media-video';
import { CmsSsrComponentProps } from '@/types/cms';

export interface FeatureContentItem {
  title?: string;
  title_style?: string;
  description?: string;
  description_style?: string;
  mobile_media_type?: 'image' | 'video';
  pc_media?: string;
  pc_media_type?: 'image' | 'video';
  mobile_media?: string;
}

export interface FeatureContentProperties {
  featured_content_items?: string; // JSON string array of content items
  background?: string;
  single_item_layout?: 'left' | 'right';
  event_key?: string;
}

export default function FeatureContent({
  properties = {},
  componentInstanceKey,
  componentKey
}: Readonly<CmsSsrComponentProps>): React.ReactElement {
  const { featured_content_items = '[]' } = properties as FeatureContentProperties;
  const contentItems = JSON.parse(featured_content_items) as FeatureContentItem[];
  return (
    <Container
      aria-label={properties?.title}
      componentKey={componentKey}
      componentInstanceKey={componentInstanceKey}
      properties={properties}
      className={twMerge('px-5 pt-5 pb-10')}>
      {contentItems.length === 1 && (
        <div>
          <div className="w-full aspect-video">
            {contentItems[0].mobile_media_type === 'image' ? (
              <MediaImage item={contentItems[0]} width={374} />
            ) : (
              <MediaVideo item={contentItems[0]} />
            )}
          </div>
          <div className="mt-5">
            <Title item={contentItems[0]} className="enki-heading-xl" />
            <Description item={contentItems[0]} className="mt-3 enki-body-sm" />
          </div>
        </div>
      )}
      {contentItems.length > 1 && (
        <div className="grid grid-cols-1 gap-10" role="list">
          {contentItems.map((item, index) => (
            <div
              key={index}
              role="listitem"
              className={twMerge('flex justify-between items-center gap-5', index % 2 === 1 && 'flex-row-reverse')}>
              <div style={{ width: 'calc((100% - 20px) / 2)' }}>
                <Title item={item} />
                <Description item={item} className="mt-3" />
              </div>
              <div className="aspect-square" style={{ width: 'calc((100% - 20px) / 2)' }}>
                {item.mobile_media_type === 'image' ? <MediaImage item={item} /> : <MediaVideo item={item} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}
